import React, { useState, useEffect } from 'react';
import { superUserAPI, superRechargeAPI } from '../../../services/api';
import { PlusIcon, MinusCircleIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import Pagination from '../../../components/common/Pagination';
import UniversalSearchInput from '../../../components/common/UniversalSearchInput';
import { formatIndianCurrency } from '../../../utils/numberFormat';

const Recharge = () => {
  const [activeTab, setActiveTab] = useState('history'); // PROMPT-079: history, requests, debit
  const [history, setHistory] = useState([]);
  const [requests, setRequests] = useState([]);
  const [debits, setDebits] = useState([]); // PROMPT-079: Add debits state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDebitModal, setShowDebitModal] = useState(false); // PROMPT-079: Add debit modal state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // PROMPT-098: Pagination state for each tab
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // PROMPT-078: Track selected user separately
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // PROMPT-078: Control dropdown visibility
  const [formData, setFormData] = useState({
    user_id: '',
    amount: '',
    remarks: ''
  });
  const [debitFormData, setDebitFormData] = useState({ // PROMPT-079: Add debit form data
    user_id: '',
    amount: '',
    reason: ''
  });

  // PROMPT-077: Search users function (updated for PROMPT-078)
  const searchUsers = async (query) => {
    // PROMPT-078: Only search when dropdown is open and query is valid
    if (!isDropdownOpen || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearchingUsers(true);
      // PROMPT-125: Use super admin user API for Super/Admin module
      const response = await superUserAPI.searchUsers(query);
      setSearchResults(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to search users:', error);
      setSearchResults([]);
    } finally {
      setIsSearchingUsers(false);
    }
  };

  // Debounced search function (updated for PROMPT-078)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers(userSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [userSearchQuery, isDropdownOpen]); // PROMPT-078: Include dropdown state in dependency

  // PROMPT-078: User selection handlers
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setFormData({ ...formData, user_id: user.id });
    setUserSearchQuery(`${user.username} - ${user.full_name || 'N/A'} (Balance: \u20B9${parseFloat(user.balance || 0).toFixed(2)})`);
    setIsDropdownOpen(false); // Close dropdown after selection
    setSearchResults([]); // Clear search results
  };

  const handleUserInputFocus = () => {
    setIsDropdownOpen(true);
    // If we have a selected user, clear the input to allow new search
    if (selectedUser) {
      setUserSearchQuery('');
      setSelectedUser(null);
      setFormData({ ...formData, user_id: '' });
    }
  };

  const handleUserInputBlur = () => {
    // Delay closing dropdown to allow click on search results
    setTimeout(() => {
      setIsDropdownOpen(false);
    }, 200);
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  // Reset to page 1 when filters or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, activeTab]);

  // Pagination handlers (client-side)
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (activeTab === 'history') {
        const response = await superRechargeAPI.getHistory(1, 100);
        let historyData = [];
        if (Array.isArray(response.data)) {
          historyData = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          historyData = response.data.data;
        }
        setHistory(historyData);
      } else if (activeTab === 'requests') {
        const response = await superRechargeAPI.getRequests(1, 100);
        let requestsData = [];
        if (response.data?.data && Array.isArray(response.data.data)) {
          requestsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          requestsData = response.data;
        }
        setRequests(requestsData);
      } else if (activeTab === 'debit') {
        const response = await superRechargeAPI.getHistory(1, 100);
        let debitsData = [];
        if (response.data?.data && Array.isArray(response.data.data)) {
          debitsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          debitsData = response.data;
        }
        setDebits(debitsData);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('Failed to fetch data');
      setHistory([]);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch users for add recharge
        if (users.length === 0) {
          const usersResponse = await superUserAPI.getAll();
          const usersData = Array.isArray(usersResponse.data) ? usersResponse.data : [];
          setUsers(usersData);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setError(`Failed to fetch data: ${error.response?.data?.error || error.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  const handleAddRecharge = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await superRechargeAPI.add({
        user_id: parseInt(formData.user_id),
        amount: parseFloat(formData.amount),
        remarks: formData.remarks
      });
      setSuccess('Recharge added successfully');
      fetchData();
      handleCloseModal();
    } catch (error) {
      setError(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleProcessRequest = async (id, action) => {
    if (!window.confirm(`Are you sure you want to ${action} this request?`)) return;
    
    try {
      await superRechargeAPI.processRequest(id, action);
      setSuccess(`Request ${action}d successfully`);
      fetchData();
    } catch (error) {
      setError(`Failed to ${action} request`);
    }
  };

  // Debit modal user search states
  const [debitUserSearchQuery, setDebitUserSearchQuery] = useState('');
  const [debitSearchResults, setDebitSearchResults] = useState([]);
  const [isDebitSearching, setIsDebitSearching] = useState(false);
  const [debitSelectedUser, setDebitSelectedUser] = useState(null);
  const [isDebitDropdownOpen, setIsDebitDropdownOpen] = useState(false);

  // Debit user search
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (!isDebitDropdownOpen || debitUserSearchQuery.length < 2) {
        setDebitSearchResults([]);
        return;
      }
      try {
        setIsDebitSearching(true);
        const response = await superUserAPI.searchUsers(debitUserSearchQuery);
        setDebitSearchResults(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        setDebitSearchResults([]);
      } finally {
        setIsDebitSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [debitUserSearchQuery, isDebitDropdownOpen]);

  const handleDebitUserSelect = (user) => {
    setDebitSelectedUser(user);
    setDebitFormData({ ...debitFormData, user_id: user.id });
    setDebitUserSearchQuery(`${user.username} - ${user.full_name || 'N/A'} (Balance: \u20B9${parseFloat(user.balance || 0).toFixed(2)})`);
    setIsDebitDropdownOpen(false);
    setDebitSearchResults([]);
  };

  const handleDebitUserInputFocus = () => {
    setIsDebitDropdownOpen(true);
    if (debitSelectedUser) {
      setDebitUserSearchQuery('');
      setDebitSelectedUser(null);
      setDebitFormData({ ...debitFormData, user_id: '' });
    }
  };

  const handleDebitUserInputBlur = () => {
    setTimeout(() => setIsDebitDropdownOpen(false), 200);
  };

  const handleDeductBalance = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await superRechargeAPI.deduct({
        user_id: parseInt(debitFormData.user_id),
        amount: parseFloat(debitFormData.amount),
        reason: debitFormData.reason
      });
      setSuccess('Balance deducted successfully');
      fetchData();
      handleCloseDebitModal();
    } catch (error) {
      setError(error.response?.data?.error || 'Deduction failed');
    }
  };

  const handleCloseDebitModal = () => {
    setShowDebitModal(false);
    setDebitFormData({ user_id: '', amount: '', reason: '' });
    setDebitUserSearchQuery('');
    setDebitSearchResults([]);
    setDebitSelectedUser(null);
    setIsDebitDropdownOpen(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      user_id: '',
      amount: '',
      remarks: ''
    });
    // PROMPT-077: Reset search state
    setUserSearchQuery('');
    setSearchResults([]);
    // PROMPT-078: Reset additional states
    setSelectedUser(null);
    setIsDropdownOpen(false);
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.username : `User #${userId}`;
  };

  // Filter history
  const filteredHistory = history.filter(record => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      getUserName(record.user_id)?.toLowerCase().includes(searchLower) ||
      record.remarks?.toLowerCase().includes(searchLower);
    const matchesUser = !selectedUser || record.user_id === selectedUser.id;
    return matchesSearch && matchesUser;
  });

  // Filter requests
  const filteredRequests = requests.filter(request => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      getUserName(request.user_id)?.toLowerCase().includes(searchLower);
    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
    const matchesUser = !selectedUser || request.user_id === selectedUser.id;
    return matchesSearch && matchesStatus && matchesUser;
  });

  // Client-side pagination based on active tab
  const activeData = activeTab === 'history' ? filteredHistory : filteredRequests;
  const clientTotalPages = Math.ceil(activeData.length / pageSize) || 1;
  const paginatedHistory = filteredHistory.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const paginatedRequests = filteredRequests.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Recharge Management</h1>
        <div className="flex gap-3">
          <button
            onClick={() => setShowDebitModal(true)}
            className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            <MinusCircleIcon className="w-5 h-5" />
            Reduce Balance
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="w-5 h-5" />
            Add Recharge
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Recharge History
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'requests'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Recharge Requests
          </button>
        </nav>
      </div>

      {/* Search and Filter */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <div className="relative">
              <UniversalSearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by user or remarks..."
              />
            </div>
        </div>

        {activeTab === 'requests' && (
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {success}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <>
          {/* Recharge History Tab */}
          {activeTab === 'history' && (
            <div className="overflow-x-auto">
              <div className="mb-4 text-sm text-gray-600">
                Showing {paginatedHistory.length} of {filteredHistory.length} records (page {currentPage} of {clientTotalPages})
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Previous Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">New Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedHistory.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        No recharge history found
                      </td>
                    </tr>
                  ) : (
                    paginatedHistory.map((record) => (
                      <tr key={record.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{getUserName(record.user_id)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm font-semibold ${record.transaction_type === 'debit' ? 'text-red-600' : 'text-green-600'}`}>
                            {record.transaction_type === 'debit' ? '-' : '+'}{formatIndianCurrency(record.amount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatIndianCurrency(record.previous_balance)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatIndianCurrency(record.new_balance)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            record.transaction_type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {record.transaction_type}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500">{record.remarks || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(record.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              
              <Pagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalRecords={filteredHistory.length}
                totalPages={clientTotalPages}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}

          {/* Recharge Requests Tab */}
          {activeTab === 'requests' && (
            <div className="overflow-x-auto">
              <div className="mb-4 text-sm text-gray-600">
                Showing {paginatedRequests.length} of {filteredRequests.length} requests (page {currentPage} of {clientTotalPages})
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedRequests.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                        No recharge requests found
                      </td>
                    </tr>
                  ) : (
                    paginatedRequests.map((request) => (
                      <tr key={request.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{getUserName(request.user_id)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-green-600">{formatIndianCurrency(request.amount)}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500">{request.remarks || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            request.status === 'approved' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {request.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(request.created_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {request.status === 'pending' && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleProcessRequest(request.id, 'approve')}
                                className="text-green-600 hover:text-green-900"
                                title="Approve"
                              >
                                <CheckCircleIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleProcessRequest(request.id, 'reject')}
                                className="text-red-600 hover:text-red-900"
                                title="Reject"
                              >
                                <XCircleIcon className="w-5 h-5" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <Pagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalRecords={filteredRequests.length}
                totalPages={clientTotalPages}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </>
      )}

      {/* Add Recharge Modal */}
      {/* Deduct Balance Modal */}
      {showDebitModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-red-700">Reduce Balance</h3>
              <button
                onClick={handleCloseDebitModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <form onSubmit={handleDeductBalance} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User *</label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    value={debitUserSearchQuery}
                    onChange={(e) => setDebitUserSearchQuery(e.target.value)}
                    onFocus={handleDebitUserInputFocus}
                    onBlur={handleDebitUserInputBlur}
                    placeholder="Type to search users..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                  {isDebitDropdownOpen && debitUserSearchQuery.length >= 2 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {isDebitSearching ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
                      ) : debitSearchResults.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">No users found</div>
                      ) : (
                        debitSearchResults.map(user => (
                          <div
                            key={user.id}
                            onClick={() => handleDebitUserSelect(user)}
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium">{user.username}</div>
                            <div className="text-gray-500">{user.email}</div>
                            <div className="text-gray-400">Balance: {formatIndianCurrency(user.balance)}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {debitFormData.user_id && (
                  <input type="hidden" name="user_id" value={debitFormData.user_id} required />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount to Deduct (₹) *</label>
                <input
                  type="number"
                  required
                  min="0.01"
                  step="0.01"
                  value={debitFormData.amount}
                  onChange={(e) => setDebitFormData({ ...debitFormData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Enter amount to deduct"
                />
                {debitSelectedUser && (
                  <p className="mt-1 text-xs text-gray-500">
                    Current balance: {formatIndianCurrency(debitSelectedUser.balance)}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                <textarea
                  rows="3"
                  required
                  value={debitFormData.reason}
                  onChange={(e) => setDebitFormData({ ...debitFormData, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Reason for deduction (required)"
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseDebitModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Deduct Balance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add Recharge</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <form onSubmit={handleAddRecharge} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    onFocus={handleUserInputFocus}
                    onBlur={handleUserInputBlur}
                    placeholder="Type to search users..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  
                  {/* PROMPT-078: Search Results Dropdown - Only show when dropdown is open */}
                  {isDropdownOpen && userSearchQuery.length >= 2 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {isSearchingUsers ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
                      ) : searchResults.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">No users found</div>
                      ) : (
                        searchResults.map(user => (
                          <div
                            key={user.id}
                            onClick={() => handleUserSelect(user)}
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium">{user.username}</div>
                            <div className="text-gray-500">{user.email}</div>
                            <div className="text-gray-400">Balance: {formatIndianCurrency(user.balance)}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {formData.user_id && (
                  <input
                    type="hidden"
                    name="user_id"
                    value={formData.user_id}
                    required
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (₹) *
                </label>
                <input
                  type="number"
                  required
                  min="0.01"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter amount"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remarks
                </label>
                <textarea
                  rows="3"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Optional remarks"
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Add Recharge
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recharge;
